import React from "react";
import classNames from "classnames";
import {
    getCountries,
    getCountryNameByCode,
    PLATFORMS,
    SourceToCountry
} from "@components/Features/catalog/catalog-utils";
import GenerationsInputOption from "@components/UI/Inputs/GenerationsInputOption/GenerationsInputOption";
import RangeInput from "@components/UI/Inputs/RangeInput/RangeInput";
import SelectInput from "@components/UI/Inputs/SelectInput/SelectInput";
import { Option } from "@components/UI/Inputs/SelectInput/types";
import { IconWithLabel } from "@src/components/UI/Inputs/IconWithLabel";
import { ColorInput } from "@app/styles/color-palette";
import { Countries } from "@features/seo/seo.constants";
import { FILTER_LABELS, YEARS_PRODUCTION } from "../constants";
import { MainSelectsProps } from "../types";
import classes from "../MainFilter.module.scss";

export const MainSelects: React.FC<MainSelectsProps> = ({
    brandValue,
    modelValue,
    generationValue,
    yearFromValue,
    yearToValue,
    mileageFromValue,
    mileageToValue,
    priceFromValue,
    priceToValue,
    sourceId,
    countryValue,
    brandsOptionsFormatted,
    modelFormattedOptions,
    generationFormattedOptions,
    filterOptionsLoading,
    customSelectComponents,
    topSelectsFontStyle,
    createSelectValue,
    createGenerationSelectValue,
    onBrandChange,
    onModelChange,
    onGenerationChange,
    onYearFromChange,
    onYearToChange,
    onMileageFromChange,
    onMileageToChange,
    onPriceFromChange,
    onPriceToChange,
    onSourceChange,
    onCountryChange
}) => {
    const sourcesOptions = countryValue
        ? PLATFORMS.filter(
              (option) => option.country === Countries[countryValue as keyof typeof Countries]
          )
        : PLATFORMS;
    const availableCountries = getCountries();
    const countriesOptions =
        sourceId && sourceId?.length > 0
            ? availableCountries.filter((option) => {
                  return sourceId.some(
                      (source: number) =>
                          SourceToCountry[source as keyof typeof SourceToCountry] === option.value
                  );
              })
            : availableCountries;
    return (
        <>
            <div className={classNames(classes.Row, classes.MainLine)}>
                <div className={classes.Col}>
                    <SelectInput
                        width="100%"
                        height={50}
                        instanceId="auto-brand"
                        className={classes.Select}
                        placeholder={FILTER_LABELS.BRAND}
                        title={FILTER_LABELS.BRAND}
                        isClearable={true}
                        components={customSelectComponents}
                        isLoading={filterOptionsLoading.brandsOptionsIsFetching}
                        value={createSelectValue(
                            brandValue?.id,
                            brandValue?.name,
                            brandValue?.alias
                        )}
                        options={brandsOptionsFormatted}
                        formatOptionLabel={(brand: Option) => <IconWithLabel option={brand} />}
                        onChange={onBrandChange}
                        fontStyles={topSelectsFontStyle}
                        borderColor={ColorInput}
                        borderRadius={10}
                    />
                </div>
                <div className={classes.Col}>
                    <SelectInput
                        width="100%"
                        height={50}
                        instanceId="model-auto"
                        className={classes.Select}
                        disabled={!brandValue}
                        isLoading={filterOptionsLoading.modelOptionsIsFetching}
                        placeholder={FILTER_LABELS.MODEL}
                        title={FILTER_LABELS.MODEL}
                        isClearable={true}
                        components={customSelectComponents}
                        value={createSelectValue(modelValue?.id, modelValue?.name)}
                        onChange={onModelChange}
                        options={modelFormattedOptions}
                        fontStyles={topSelectsFontStyle}
                        borderColor={ColorInput}
                        borderRadius={10}
                    />
                </div>
                <div className={classes.Col}>
                    <SelectInput
                        width="100%"
                        height={50}
                        instanceId="generation-auto"
                        className={classes.Select}
                        disabled={!modelValue}
                        title={FILTER_LABELS.GENERATION}
                        placeholder={FILTER_LABELS.GENERATION}
                        isClearable={true}
                        components={{
                            ...customSelectComponents,
                            Option: GenerationsInputOption
                        }}
                        value={createGenerationSelectValue(generationValue)}
                        onChange={onGenerationChange}
                        isLoading={filterOptionsLoading.isFetchingGenerations}
                        options={generationFormattedOptions}
                        fontStyles={topSelectsFontStyle}
                        borderColor={ColorInput}
                        borderRadius={10}
                    />
                </div>
            </div>

            <div className={classNames(classes.Row, classes.MainLine)}>
                <div className={classes.Col}>
                    <RangeInput
                        useSelectMode={true}
                        firstSelect={
                            <SelectInput
                                width="100%"
                                height={50}
                                title={FILTER_LABELS.YEAR_FROM}
                                placeholder={FILTER_LABELS.YEAR_FROM}
                                isClearable={true}
                                instanceId="year-from-auto"
                                className={classes.Select}
                                options={YEARS_PRODUCTION}
                                borderColor={ColorInput}
                                fontStyles={topSelectsFontStyle}
                                value={createSelectValue(yearFromValue)}
                                onChange={onYearFromChange}
                            />
                        }
                        secondSelect={
                            <SelectInput
                                width="100%"
                                height={50}
                                isClearable={true}
                                title={FILTER_LABELS.YEAR_TO}
                                placeholder={FILTER_LABELS.YEAR_TO}
                                instanceId="year-to-auto"
                                className={classes.Select}
                                options={YEARS_PRODUCTION}
                                borderColor={ColorInput}
                                fontStyles={topSelectsFontStyle}
                                value={createSelectValue(yearToValue)}
                                onChange={onYearToChange}
                            />
                        }
                    />
                </div>
                <div className={classes.Col}>
                    <RangeInput
                        fromLabel={FILTER_LABELS.MILEAGE_FROM}
                        toLabel={FILTER_LABELS.MILEAGE_TO}
                        onFromChanged={onMileageFromChange}
                        onToChanged={onMileageToChange}
                        fromVal={mileageFromValue}
                        toVal={mileageToValue}
                        maxLen={7}
                    />
                </div>
                <div className={classes.Col}>
                    <RangeInput
                        fromLabel={FILTER_LABELS.PRICE_FROM}
                        toLabel={FILTER_LABELS.PRICE_TO}
                        onFromChanged={onPriceFromChange}
                        onToChanged={onPriceToChange}
                        fromVal={priceFromValue}
                        toVal={priceToValue}
                        maxLen={9}
                    />
                </div>
            </div>
            <div className={classNames(classes.Row, classes.MainLine)}>
                <div className={classNames(classes.Col, classes.SourcesCol)}>
                    <SelectInput
                        width="100%"
                        height={50}
                        instanceId="source-auto"
                        title={FILTER_LABELS.SOURCE}
                        className={classes.Select}
                        placeholder={FILTER_LABELS.PLATFORM}
                        isClearable={true}
                        isMulti={true}
                        value={PLATFORMS.filter((option) => sourceId?.includes(option.value))}
                        options={sourcesOptions}
                        onChange={onSourceChange}
                        borderColor={ColorInput}
                        fontStyles={topSelectsFontStyle}
                    />
                </div>
                <div className={classes.Col}>
                    <SelectInput
                        width="100%"
                        height={50}
                        instanceId="country-auto"
                        title={FILTER_LABELS.COUNTRY}
                        className={classes.Select}
                        placeholder={FILTER_LABELS.COUNTRY}
                        isClearable={true}
                        isMulti={false}
                        value={
                            countryValue
                                ? {
                                      value: countryValue,
                                      label: getCountryNameByCode(countryValue as Countries)
                                  }
                                : undefined
                        }
                        options={countriesOptions}
                        onChange={onCountryChange}
                        borderColor={ColorInput}
                        fontStyles={topSelectsFontStyle}
                    />
                </div>
                <div className={classes.Col}></div>
            </div>
        </>
    );
};